import React from 'react';

/**
 * Repository in, harness out — the product in one drawing.
 *
 * Lived in GitTimeline.jsx until the homepage wanted it too. It is the one
 * place on the site that explains the whole thing without a paragraph, which
 * is exactly why it should not have been stuck on a page a visitor reaches
 * second.
 */
/** The pipeline as one flat vector drawing: a repository goes in, Loreto reads
 *  it, a harness lands on disk. Authored rather than generated — three passes
 *  through an image model put invented words on every folder and document,
 *  which is what those models do with anything that looks like paper. Drawn
 *  here it is crisp at any size, weighs nothing, and uses the brand palette
 *  exactly. The labels are real text underneath, not baked into the art. */
const HarnessArt = () => (
  <svg viewBox="0 0 1200 300" className="w-full h-auto block" role="img"
       aria-label="A repository folder, the Loreto mark, and the harness files it writes">
    <defs>
      <linearGradient id="hfFolder" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#C4643A" /><stop offset="100%" stopColor="#8E4526" />
      </linearGradient>
      <linearGradient id="hfPaper" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#F7F3EC" /><stop offset="100%" stopColor="#E4DCCE" />
      </linearGradient>
      <linearGradient id="hfDisc" x1="0.2" y1="0" x2="0.8" y2="1">
        <stop offset="0%" stopColor="#E0825F" /><stop offset="100%" stopColor="#A8481F" />
      </linearGradient>
      <filter id="hfShadow" x="-30%" y="-30%" width="160%" height="170%">
        <feDropShadow dx="0" dy="10" stdDeviation="14" floodColor="#000" floodOpacity="0.55" />
      </filter>
    </defs>

    {/* soft blobs, rust only — the palette has one accent and this keeps it */}
    <g opacity="0.14">
      <ellipse cx="215" cy="150" rx="185" ry="118" fill="#B8542A" />
      <ellipse cx="600" cy="145" rx="120" ry="112" fill="#7A4B38" />
      <ellipse cx="985" cy="152" rx="175" ry="115" fill="#B8542A" />
    </g>

    {/* in — GitHub itself, in the same flat vector language as the rest.
        ICON_SCALE shrinks each mark about its own centre. They were drawn to
        nearly fill their haloes, which read as three huge stickers rather than
        a diagram; held smaller the halo becomes the shape and the mark becomes
        the subject. */}
    <g filter="url(#hfShadow)" transform="translate(200 150) scale(0.7) translate(-200 -150)">
      <rect x="128" y="78" width="144" height="144" rx="30" fill="url(#hfFolder)" />
      <rect x="128" y="78" width="144" height="144" rx="30" fill="none" stroke="#F0D9CB" strokeOpacity="0.28" strokeWidth="2" />
      <g transform="translate(157 107) scale(3.583)" fill="#FCF6F0">
        <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
      </g>
    </g>
    {/* a few commits leaving the repository, toward Loreto */}
    <g opacity="0.75">
      <circle cx="300" cy="150" r="5" fill="#DD785F" />
      <circle cx="326" cy="150" r="4" fill="#DD785F" opacity="0.7" />
      <circle cx="350" cy="150" r="3" fill="#DD785F" opacity="0.45" />
    </g>

    {/* through — the mark, on a disc */}
    <g filter="url(#hfShadow)" transform="translate(600 150) scale(0.7) translate(-600 -150)">
      <circle cx="600" cy="150" r="66" fill="url(#hfDisc)" />
      <circle cx="600" cy="150" r="66" fill="none" stroke="#F0D9CB" strokeOpacity="0.5" strokeWidth="2" />
      <text x="600" y="150" textAnchor="middle" dominantBaseline="central"
            fontFamily="Instrument Serif, Georgia, serif" fontSize="62" fill="#FCF6F0">L</text>
      <rect x="576" y="188" width="48" height="3" rx="1.5" fill="#FCF6F0" opacity="0.85" />
    </g>

    {/* out — the harness, three sheets fanned */}
    <g filter="url(#hfShadow)" transform="translate(1010 148) scale(0.7) translate(-1010 -148)">
      <rect x="912" y="92" width="168" height="122" rx="12" fill="#D9D0C2" transform="rotate(-7 996 153)" />
      <rect x="926" y="86" width="168" height="122" rx="12" fill="#EAE2D5" transform="rotate(-2.5 1010 147)" />
      <rect x="940" y="82" width="168" height="122" rx="12" fill="url(#hfPaper)" />
      <g fill="#B08A6E">
        <rect x="958" y="104" width="78" height="7" rx="3.5" />
        <rect x="958" y="123" width="122" height="7" rx="3.5" />
        <rect x="958" y="142" width="96" height="7" rx="3.5" />
        <rect x="958" y="161" width="130" height="7" rx="3.5" />
      </g>
      <rect x="958" y="104" width="78" height="7" rx="3.5" fill="#C4643A" />
    </g>

    {/* the two arrows */}
    <g stroke="#8A8177" strokeWidth="2.5" fill="none" strokeLinecap="round">
      <path d="M400 150h67" /><path d="M720 150h77" />
    </g>
    <g fill="#DD785F">
      <path d="M478 150l-11-6v12z" /><path d="M808 150l-11-6v12z" />
    </g>

    {/* a little life, in the accent */}
    <g fill="#DD785F" opacity="0.9">
      <path d="M352 62l4 10 10 4-10 4-4 10-4-10-10-4 10-4z" />
      <path d="M860 232l3 7 7 3-7 3-3 7-3-7-7-3 7-3z" opacity="0.7" />
      <path d="M676 74l3 7 7 3-7 3-3 7-3-7-7-3 7-3z" opacity="0.6" />
    </g>
  </svg>
);

/** The drawing, then the words. Kept apart so the labels stay real text:
 *  selectable, translatable, and sharp on a phone. */
export default function HarnessFlow() {
  return (
  <div className="mt-10 md:mt-12">
    <HarnessArt />
    {/* three columns at every width, so each label stays under the object it
        names. Stacking them on a phone broke that pairing and the drawing
        stopped explaining anything. */}
    <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-4">
      {[
        ['Your code', ['github.com/you/api']],
        ['Loreto reads it', ['every branch', 'every release', 'every revert']],
        ['Your harness', ['AGENTS.md', 'SKILL.md files', 'references/', 'tests/']],
      ].map(([head, items]) => (
        <div key={head} className="text-center min-w-0">
          <div className="serif text-[1rem] sm:text-[1.3rem] md:text-[1.45rem] leading-tight text-[var(--text)]">{head}</div>
          {/* the output is a package, so it is listed rather than run together
              in one line — a reader should see how many pieces come back */}
          <ul className="mt-2 inline-block text-left">
            {items.map((it) => (
              <li key={it} className="mono text-[0.62rem] sm:text-[0.72rem] md:text-[0.76rem] leading-[1.7] text-[var(--text-3)] break-words">
                <span className="text-[var(--accent)] mr-1.5">&middot;</span>{it}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </div>
  );
}
